/* rb-b-bis.js — let shoppers ask to be told when a SOLD-OUT product returns.

   DESKTOP ONLY (>=981px), matching rb-b-bis.css. MOBILE MUST BE IDENTICAL TO
   THE LIVE SITE — Mark's instruction — so this does not run there, and mobile
   keeps the theme's own behaviour (which is: nothing on a sold-out card).
   An earlier version gated at 768, which left tablets 768-980 getting a
   feature no other Direction B module applies at that width. Both the JS and
   the CSS now cut at 981; keep them in step.

   THE GAP. The theme renders a quick-add overlay only on cards with something
   buyable. A fully sold-out product therefore offers nothing on hover: no
   sizes, no notify, no overlay in the DOM at all (measured on footwear page 2:
   the MOMA Allacciata card has no .js-quick-add-overlay, no
   script.js-quick-add-variants and no mobile trigger). Meanwhile
   soldout-flow-spec-2026-07-10 expects these products to stay published so
   "Klaviyo back-in-stock collects demand" — which nothing was collecting.

   ONE STEP, NOT TWO. The first version made the shopper pick a size and THEN
   type an email. Wrong model: on an in-stock product choosing a size is the
   purchase decision, but when every size is gone it is a question with no
   good answer. It opens straight to the email field, size optional.

   "ANY SIZE" REALLY MEANS ALL OF THEM. Klaviyo's back-in-stock subscription is
   per catalog-variant; there is no product-level equivalent. Subscribing to a
   single representative variant would quietly mean "tell me when a 42
   returns" while the shopper believed they had asked about the shoe, so Any
   size posts one subscription per size. The trade-off: an Any size shopper
   hears about every size that comes back. That is why the dropdown exists.

   WHY A LAYER, NOT LIQUID: the overlay comes from
   snippets/product-grid-item.liquid at 61KB, and whole-file replacement means
   retyping it into a GraphQL payload — the transcription risk this project
   has avoided all along with the 89KB collection section.

   IT REUSES THE STORE'S EXISTING PLUMBING: the same Klaviyo client endpoints,
   revision and payload shape as rb-quick-add-sheet.js and
   pdp-back-in-stock.js, and the same .js-rb-qas-config node, verified present
   on collection pages with both klaviyo_public_key and newsletter_list_id.

   IT VERIFIES BEFORE IT ACTS. "No overlay" is the trigger to look, not proof
   the product is dead; the panel is built only once /products/<handle>.js
   confirms every variant is unavailable. The fetch is on hover intent, cached
   per handle, never on load. */

(function () {
  'use strict';

  var DESKTOP = '(min-width: 981px)';   // keep in step with rb-b-bis.css
  var HOVER_DELAY = 120;                // hover intent, so sweeping fetches nothing
  var MAX_SUBS = 30;                    // sanity cap on an "Any size" fan-out

  /* MOBILE PARITY GUARD — see the note at the top. */
  if (!window.matchMedia(DESKTOP).matches) return;

  var cache = {};
  var config = null;

  function cfg() {
    if (config) return config;
    var node = document.querySelector('.js-rb-qas-config');
    try { config = node ? JSON.parse(node.textContent) : {}; }
    catch (e) { config = {}; }
    return config;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function handleOf(card) {
    var a = card.querySelector('a[href*="/products/"]');
    if (!a) return null;
    var href = a.getAttribute('href') || '';
    var part = href.split('/products/')[1];
    return part ? part.split('?')[0].split('#')[0] : null;
  }

  function load(handle) {
    if (Object.prototype.hasOwnProperty.call(cache, handle)) {
      return Promise.resolve(cache[handle]);
    }
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { cache[handle] = j || false; return cache[handle]; })
      .catch(function () { cache[handle] = false; return false; });
  }

  /* One back-in-stock subscription per variant id, plus one newsletter
     subscription. Same calls the mobile sheet makes. */
  function subscribe(email, variantIds) {
    var c = cfg();
    if (!c.klaviyo_public_key) return Promise.reject(new Error('no key'));
    var key = encodeURIComponent(c.klaviyo_public_key);
    var jobs = [];

    variantIds.slice(0, MAX_SUBS).forEach(function (id) {
      jobs.push(fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + key, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: { data: { type: 'profile', attributes: { email: email } } }
            },
            relationships: {
              variant: {
                data: { type: 'catalog-variant', id: '$shopify:::$default:::' + id }
              }
            }
          }
        })
      }));
    });

    if (c.newsletter_list_id) {
      jobs.push(fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + key, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email: email } } },
              custom_source: 'Collection back-in-stock'
            },
            relationships: { list: { data: { type: 'list', id: c.newsletter_list_id } } }
          }
        })
      }));
    }

    return Promise.all(jobs).then(function (rs) {
      if (rs.some(function (r) { return !r.ok; })) throw new Error('klaviyo');
    });
  }

  /* variant.title is the WHOLE variant name — "5 / Blue/ White / 42" on the
     MOMA shoes — so use the size OPTION instead. product.options is a list of
     names in some Shopify versions and objects in others; handle both, then
     fall back to the last option, where size sits on every multi-option
     product here. */
  function sizeIndex(product) {
    var names = (product.options || []).map(function (o) {
      return String(o && o.name ? o.name : o).toLowerCase();
    });
    for (var i = 0; i < names.length; i++) {
      if (names[i].indexOf('size') !== -1) return i;
    }
    return names.length > 1 ? names.length - 1 : -1;
  }

  function build(card, product) {
    if (card.querySelector('.rbb-bis')) return;

    var host = card.querySelector('.product-card-top') || card;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';

    var raw = (product.variants || []).filter(function (v) { return v && v.id; });
    if (!raw.length) return;

    /* One entry per size. A sold-out product can carry the same size in
       several colourways; keep every id, because Any size subscribes to all. */
    var idx = sizeIndex(product);
    var order = [];
    var byLabel = {};
    raw.forEach(function (v) {
      var label = (idx >= 0 && v.options && v.options[idx] != null)
        ? String(v.options[idx]).trim()
        : String(v.title || '').trim();
      if (!label) return;
      var key = label.toLowerCase();
      if (!byLabel[key]) { byLabel[key] = { label: label, ids: [] }; order.push(key); }
      byLabel[key].ids.push(v.id);
    });
    if (!order.length) return;

    var sizes = order.map(function (k) { return byLabel[k]; });
    var allIds = raw.map(function (v) { return v.id; });
    var hasChoice = sizes.length > 1 && idx >= 0;

    var panel = document.createElement('div');
    panel.className = 'rbb-bis';
    panel.innerHTML =
      '<p class="rbb-bis__label">Sold out &mdash; get notified</p>' +
      '<p class="rbb-bis__msg">We&rsquo;ll email you when it&rsquo;s back.</p>' +
      '<div class="rbb-bis__row">' +
        '<input type="email" class="rbb-bis__email" placeholder="you@email.com" ' +
               'autocomplete="email" aria-label="Email for back-in-stock alert">' +
        '<button type="button" class="rbb-bis__go">Notify</button>' +
      '</div>' +
      (hasChoice
        ? '<label class="rbb-bis__sizewrap">Size ' +
            '<select class="rbb-bis__select" aria-label="Size to be notified about">' +
              '<option value="">Any size</option>' +
              sizes.map(function (s, i) {
                return '<option value="' + i + '">' + esc(s.label) + '</option>';
              }).join('') +
            '</select>' +
          '</label>'
        : '');

    var msg = panel.querySelector('.rbb-bis__msg');
    var email = panel.querySelector('.rbb-bis__email');
    var go = panel.querySelector('.rbb-bis__go');
    var select = panel.querySelector('.rbb-bis__select');

    /* The whole card is wrapped in a product link, so without this every
       click in here throws the shopper onto the PDP mid-signup. */
    panel.addEventListener('click', function (e) {
      e.stopPropagation();
      e.preventDefault();
    });

    function send() {
      var value = (email.value || '').trim();
      if (!value || value.indexOf('@') < 1) { email.focus(); return; }

      var pick = select && select.value !== '' ? sizes[parseInt(select.value, 10)] : null;
      var ids = pick ? pick.ids : allIds;
      if (!ids.length) return;

      panel.classList.remove('is-error');
      go.disabled = true;

      subscribe(value, ids)
        .then(function () {
          panel.classList.add('is-done');
          msg.textContent = pick
            ? 'Done — we’ll email you when size ' + pick.label + ' is back.'
            : 'Done — we’ll email you when it’s back.';
        })
        .catch(function () {
          panel.classList.add('is-error');
          msg.textContent = 'Sorry, that didn’t go through. Try again in a moment.';
        })
        .then(function () { go.disabled = false; });
    }

    go.addEventListener('click', send);
    email.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); send(); }
    });

    /* THE PANEL HAS TO CLOSE AGAIN. It is revealed by `.product-card:hover`
       OR `.rbb-bis:focus-within`, and a native <select> KEEPS FOCUS after you
       choose from it — so picking a size left :focus-within true forever and
       the panel sat open over the grid with the pointer on another card.
       Blurring the select hands control back to hover. */
    if (select) {
      select.addEventListener('change', function () { select.blur(); });
    }

    /* Leaving the card closes it — UNLESS an email is half-typed and still
       focused, which would be an infuriating thing to throw away. */
    card.addEventListener('mouseleave', function () {
      var active = document.activeElement;
      var midSignup = !panel.classList.contains('is-done') &&
                      active === email && (email.value || '').trim();
      if (midSignup) return;
      if (panel.contains(active) && active.blur) active.blur();
    });

    host.appendChild(panel);
  }

  function consider(card) {
    if (!window.matchMedia(DESKTOP).matches) return;   // also covers a resize
    if (card.dataset.rbbBis) return;
    if (card.querySelector('.js-quick-add-overlay')) return;   // theme has it covered
    if (card.querySelector('.rbb-bis')) return;

    var handle = handleOf(card);
    if (!handle) return;

    card.dataset.rbbBis = 'pending';
    load(handle).then(function (product) {
      if (!product || !product.variants || !product.variants.length) {
        card.dataset.rbbBis = 'skip';
        return;
      }
      // "No overlay" was the hint; THIS is the proof.
      var anyAvailable = product.variants.some(function (v) { return v && v.available; });
      if (anyAvailable) { card.dataset.rbbBis = 'skip'; return; }

      card.dataset.rbbBis = 'done';
      build(card, product);
    });
  }

  function boot() {
    var timer = null;
    document.addEventListener('pointerover', function (e) {
      if (!e.target || !e.target.closest) return;
      var card = e.target.closest('.js-product-card, .product-card');
      if (!card) return;
      clearTimeout(timer);
      timer = setTimeout(function () { consider(card); }, HOVER_DELAY);
    }, { passive: true });

    /* Keyboard users never fire pointerover, and tabbing into a card's link
       is exactly when they would want this. */
    document.addEventListener('focusin', function (e) {
      if (!e.target || !e.target.closest) return;
      var card = e.target.closest('.js-product-card, .product-card');
      if (card) consider(card);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
